import { useCallback, useEffect, useMemo, useState } from 'react'

import isEmpty from 'lodash.isempty'
import throttle from 'lodash.throttle'

import { SuggestOptions, Suggest } from 'app/services/Geosuggest'
import { LOQATE_ITEM_TYPES } from 'app/services/Geosuggest/Geosuggest.types'
import {
  formatSelectedGeoSuggestLabel,
  getAddressObject
} from 'app/services/Geosuggest/Geosuggest.utils'
import { isOfType } from 'app/utils/helpers/type.helpers'

import useGeosuggest from './useGeosuggest'

export type UseGeosuggestInput = {
  value?: GeosuggestValue | string | null
  onChange: (value: GeosuggestValue | null | any) => void
  options?: SuggestOptions
  throttleDelay?: number
  resetInputAfterSelect?: boolean
  onAfterSelect?: () => void
}

const useGeosuggestInput = ({
  value = '',
  options = {},
  onChange,
  throttleDelay = 200,
  resetInputAfterSelect,
  onAfterSelect
}: UseGeosuggestInput) => {
  const { getGeosuggest, loaded } = useGeosuggest()

  const [inputValue, setInputValue] = useState(() =>
    getInputValueFromValue(value)
  )
  const [suggestOptions, setSuggestOptions] = useState<Suggest[]>([])

  // set actual value
  useEffect(() => {
    setInputValue(getInputValueFromValue(value))
  }, [value])

  // suggest with throttle
  const searchSuggests = useMemo(
    () =>
      throttle(async (input: string, callback: (resp) => void) => {
        try {
          if (!input && !loaded) return

          const geosuggest = await getGeosuggest()
          const resp = await geosuggest?.searchSuggests(input)

          callback(resp || null)
        } catch (error) {
          console.error(error)
        }
      }, throttleDelay),
    [loaded, options]
  )

  // suggest when change input value
  useEffect(() => {
    let active = true

    if (inputValue === '') {
      setSuggestOptions([])
    }

    searchSuggests(inputValue, (results: Suggest[]) => {
      if (active) {
        setSuggestOptions(results || [])
      }
    })

    return () => {
      active = false
    }
  }, [inputValue])

  const onChangeInput = useCallback((value: UseGeosuggestInput['value']) => {
    setInputValue(getInputValueFromValue(value))
  }, [])

  const onSelect = useCallback(
    async (suggest: Suggest | null) => {
      if (suggest) {
        const geosuggest = await getGeosuggest()

        const isSuggestContainer =
          suggest.loqateType !== LOQATE_ITEM_TYPES.address

        if (isSuggestContainer) {
          const containerSuggests = await geosuggest?.searchSuggests(
            inputValue,
            suggest.loqateId
          )

          if (containerSuggests?.length) {
            setSuggestOptions(containerSuggests)
          }

          return
        }

        const geocodedSuggest = await geosuggest?.getDetails(suggest)

        if (!isEmpty(geocodedSuggest)) {
          const address = getAddressObject({
            addressObject: geocodedSuggest?.loqateAddress
          })

          onChange({
            label: formatSelectedGeoSuggestLabel(address) || '',
            location: geocodedSuggest?.location,
            description: geocodedSuggest?.description || '',
            address
          })
        } else {
          onChange({
            label: suggest.label || '',
            location: suggest.location,
            description: suggest.description || ''
          })
        }
      } else {
        onChange(null)
      }

      if (onAfterSelect) onAfterSelect()

      if (resetInputAfterSelect) {
        setInputValue('')
      }
    },
    [onChange, options, getGeosuggest, resetInputAfterSelect]
  )

  return { suggestOptions, onChangeInput, onSelect, inputValue }
}

const getInputValueFromValue = (value?: UseGeosuggestInput['value']) =>
  isOfType.string(value) ? value : value?.label || ''

export default useGeosuggestInput
